/**
 * Notice Service — remote notice channel client (ADR-007, P4-d, BOARD #42).
 *
 * Chain: front-door Worker `/notices/v1` (P4-a; NOTICES_BASE_URL set then)
 *   → edge function `reedle-notices`
 *   → direct `app_notices` table read (anon policy).
 * First success wins; total failure returns [] (never blocks boot).
 */

import { apiConfig } from '../lib/apiConfig';
import { supabase } from '../lib/supabase';
import { getLocalVersion } from './versionService';

// Set by P4-a to the front-door origin (e.g. https://api-reedle.dimp.studio).
// Null until then: clients go straight to the edge function.
const NOTICES_BASE_URL = null;

function clientParams() {
  let locale = 'en';
  try {
    locale = (navigator.language || 'en').split('-')[0];
  } catch {
    // ignore
  }
  let region = 'global';
  try {
    region = apiConfig.region || 'global';
  } catch {
    // ignore
  }
  return {
    app: 'reedle',
    channel: 'web',
    version: getLocalVersion(),
    locale,
    region,
  };
}

async function fetchJson(url, timeoutMs = 8000, bearer = null) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const headers = bearer ? { Authorization: `Bearer ${bearer}` } : {};
    const res = await fetch(url, { signal: ctrl.signal, headers });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Session JWT for internal-audience notices; null when signed out. */
async function sessionBearer() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Fetch active notices for this client. Never throws; [] on any failure.
 * P4-d2: the session JWT (if any) is sent so the edge can serve 'internal'
 * rows to allowlisted testers; the direct-table fallback only reads 'all',
 * so internal rows can never leak through it.
 */
export async function getActiveNotices() {
  const p = new URLSearchParams(clientParams());
  const bearer = await sessionBearer();

  if (NOTICES_BASE_URL) {
    const via = await fetchJson(`${NOTICES_BASE_URL}/notices/v1?${p}`, 8000, bearer);
    if (via && Array.isArray(via.notices)) return via.notices;
  }

  const edge = await fetchJson(`${apiConfig.supabaseUrl}/functions/v1/reedle-notices?${p}`, 8000, bearer);
  if (edge && Array.isArray(edge.notices)) return edge.notices;

  for (const withAudience of [true, false]) {
    try {
      const now = new Date().toISOString();
      const sel = withAudience ? 'id,severity,title,body,audience' : 'id,severity,title,body';
      let q = supabase
        .from('app_notices')
        .select(sel)
        .eq('app_name', 'reedle')
        .eq('active', true);
      if (withAudience) q = q.eq('audience', 'all');
      const { data, error } = await q
        .or(`starts_at.is.null,starts_at.lte.${now}`)
        .or(`ends_at.is.null,ends_at.gt.${now}`)
        .order('created_at', { ascending: false })
        .limit(5);
      if (!error) return data || [];
      if (!String(error.message || '').includes('audience')) return [];
      // column missing (migration not run yet) → retry without it
    } catch {
      return [];
    }
  }
  return [];
}

const DISMISS_KEY = 'reedle-notices-dismissed';

/** Ids the user dismissed (persisted; severity=block is never dismissible). */
export function getDismissedIds() {
  try {
    return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]');
  } catch {
    return [];
  }
}

export function dismissNotice(id) {
  try {
    const ids = getDismissedIds();
    if (!ids.includes(id)) {
      ids.push(id);
      localStorage.setItem(DISMISS_KEY, JSON.stringify(ids.slice(-50)));
    }
  } catch {
    // ignore
  }
}
